import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 4;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

class RecentlyViewedProducts extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#loadProducts();
  }

  async #loadProducts() {
    const { sectionId, productId, maxProducts } = this.dataset;

    if (!sectionId) {
      throw new Error('Section ID is required');
    }

    let viewedProducts = RecentlyViewed.getProducts();

    // Filter out the current product if we are on a product page
    if (productId) {
      viewedProducts = viewedProducts.filter((id) => id !== productId);
    }

    if (viewedProducts.length === 0) {
      this.dataset.hasProducts = 'false';
      if (!Shopify.designMode) {
        this.classList.add('hidden');
      }
      return;
    }

    const limit = parseInt(maxProducts) || 4;
    const limitedProducts = viewedProducts.slice(0, limit);

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', limitedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`);
      }
      const html = await response.text();
      if (html.trim().length) {
        this.dataset.hasProducts = 'true';
        this.classList.remove('hidden');
        morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      } else {
        this.#handleError(new Error('No markup returned for recently viewed products'));
      }
    } catch (e) {
      this.#handleError(e);
    }
  }

  #handleError(error) {
    console.error('Recently viewed products error:', error.message);
    this.classList.add('hidden');
    this.dataset.hasProducts = 'false';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
